import { useEffect, useState } from 'react'
import { ApiFehler, alsUhrzeit, api } from '../../api/client'
import type { Tauschpartner, Vorschlag, Zeile } from './typen'

interface Eigenschaften {
  datum: string
  zeile: Zeile
  onFertig: () => void
  onAbbruch: () => void
}

/**
 * Befunde, die am Kurs selbst hängen und nicht am Kandidaten. Sie gelten für jede
 * Besetzung gleichermaßen und werden deshalb einmal über den Listen gezeigt.
 */
const KURSBEZOGEN = new Set(['PAUSE_ZU_KURZ', 'TEILPAUSE_ZU_KURZ'])

/**
 * Besetzt einen Kurs. Zeigt geprüfte Vorschlagslisten: geeignete Kandidaten zuerst,
 * ungeeignete mit Grund — der Disponent soll sehen, warum jemand ausfällt.
 */
export function Besetzungsdialog({ datum, zeile, onFertig, onAbbruch }: Eigenschaften) {
  const [fahrer, setFahrer] = useState<Vorschlag[]>([])
  const [fahrzeuge, setFahrzeuge] = useState<Vorschlag[]>([])
  const [gewaehlterFahrer, setGewaehlterFahrer] = useState<number | null>(zeile.fahrerId)
  const [gewaehltesFahrzeug, setGewaehltesFahrzeug] = useState<number | null>(zeile.fahrzeugId)
  const [status, setStatus] = useState(zeile.status ?? 'GEPLANT')
  const [bemerkung, setBemerkung] = useState(zeile.bemerkung ?? '')
  const [laedt, setLaedt] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)
  const [sperrmeldung, setSperrmeldung] = useState<string | null>(null)
  const [speichert, setSpeichert] = useState(false)

  // Tausch: eigener Reiter, weil er einen anderen Vorgang beschreibt. Beim Besetzen wählt
  // der Disponent einen Fahrer für diesen Kurs; beim Tauschen wählt er einen anderen
  // Dienst, und beide Seiten wechseln gemeinsam.
  const [reiter, setReiter] = useState<'besetzen' | 'tauschen'>('besetzen')
  const [partner, setPartner] = useState<Tauschpartner[] | null>(null)
  const [gewaehlterPartner, setGewaehlterPartner] = useState<number | null>(null)
  const [tauschsperre, setTauschsperre] = useState<string | null>(null)

  // Aus einem beliebigen Vorschlag ziehen — kursbezogene Befunde sind bei allen gleich.
  const kursbefunde = (fahrer[0]?.befunde ?? fahrzeuge[0]?.befunde ?? []).filter((b) =>
    KURSBEZOGEN.has(b.regel),
  )

  useEffect(() => {
    Promise.all([
      api.get<Vorschlag[]>(
        `/api/disposition/fahrervorschlaege?datum=${datum}&kursId=${zeile.kursId}`,
      ),
      api.get<Vorschlag[]>(
        `/api/disposition/fahrzeugvorschlaege?datum=${datum}&kursId=${zeile.kursId}`,
      ),
    ])
      .then(([f, fz]) => {
        setFahrer(f)
        setFahrzeuge(fz)
      })
      .catch((ex) => setFehler(ex instanceof Error ? ex.message : 'Laden fehlgeschlagen'))
      .finally(() => setLaedt(false))
  }, [datum, zeile.kursId])

  // Erst beim Öffnen des Reiters laden: Die Probe rechnet für jeden Kandidaten beide
  // Seiten des Tauschs durch — das gehört nicht in jeden Aufruf des Dialogs.
  useEffect(() => {
    if (reiter !== 'tauschen' || partner !== null) {
      return
    }
    api
      .get<Tauschpartner[]>(
        `/api/disposition/tauschpartner?datum=${datum}&kursId=${zeile.kursId}`,
      )
      .then(setPartner)
      .catch((ex) =>
        setFehler(ex instanceof Error ? ex.message : 'Tauschpartner nicht ladbar'),
      )
  }, [reiter, partner, datum, zeile.kursId])

  async function tauschen(trotzdem: boolean) {
    if (gewaehlterPartner === null) {
      return
    }
    setSpeichert(true)
    setFehler(null)
    setTauschsperre(null)
    try {
      await api.post('/api/disposition/tauschen', {
        betriebstag: datum,
        kursIdVon: zeile.kursId,
        kursIdNach: gewaehlterPartner,
        trotzdem,
      })
      onFertig()
    } catch (ex) {
      const text = ex instanceof Error ? ex.message : 'Tausch fehlgeschlagen'
      if (ex instanceof ApiFehler && ex.status === 409) {
        setTauschsperre(text)
      } else {
        setFehler(text)
      }
    } finally {
      setSpeichert(false)
    }
  }

  async function speichern(trotzdem: boolean) {
    setSpeichert(true)
    setFehler(null)
    setSperrmeldung(null)
    try {
      await api.post('/api/disposition/besetzen', {
        betriebstag: datum,
        kursId: zeile.kursId,
        fahrerId: gewaehlterFahrer,
        fahrzeugId: gewaehltesFahrzeug,
        status,
        bemerkung: bemerkung || null,
        trotzdem,
      })
      onFertig()
    } catch (ex) {
      const text = ex instanceof Error ? ex.message : 'Speichern fehlgeschlagen'
      // Ein 409 kommt von einer Sperre — dann Übersteuerung anbieten.
      if (ex instanceof ApiFehler && ex.status === 409) {
        setSperrmeldung(text)
      } else {
        setFehler(text)
      }
    } finally {
      setSpeichert(false)
    }
  }

  function liste(
    vorschlaege: Vorschlag[],
    gewaehlt: number | null,
    setzen: (id: number | null) => void,
    leertext: string,
  ) {
    const geeignet = vorschlaege.filter((v) => v.geeignet)
    const rest = vorschlaege.filter((v) => !v.geeignet)

    /* Kursbezogene Befunde bei jedem Kandidaten zu wiederholen wäre Rauschen —
       sie stehen einmal über der Liste. */
    const zeigen = (v: Vorschlag) => v.befunde.filter((b) => !KURSBEZOGEN.has(b.regel))

    return (
      // Bewusst ohne eigene Höhenbegrenzung: Die Liste wächst mit, gescrollt wird
      // nur im Inhaltsbereich der Seite.
      <div
        style={{
          border: '1px solid var(--grau-200)',
          borderRadius: 'var(--radius-klein)',
          overflow: 'hidden',
        }}
      >
        <label
          style={{
            display: 'flex',
            gap: 9,
            padding: '9px 12px',
            borderBottom: '1px solid var(--grau-100)',
            fontWeight: 400,
            cursor: 'pointer',
            marginBottom: 0,
          }}
        >
          <input
            type="radio"
            style={{ width: 'auto' }}
            checked={gewaehlt === null}
            onChange={() => setzen(null)}
          />
          <span style={{ color: 'var(--grau-500)' }}>{leertext}</span>
        </label>

        {vorschlaege.length === 0 && (
          <div style={{ padding: 14, color: 'var(--grau-500)', fontSize: '0.86rem' }}>
            Keine Kandidaten. Prüfen Sie Teamzuordnung und Kursberechtigungen.
          </div>
        )}

        {[...geeignet, ...rest].map((v) => (
          <label
            key={v.id}
            style={{
              display: 'flex',
              gap: 9,
              padding: '9px 12px',
              borderBottom: '1px solid var(--grau-100)',
              fontWeight: 400,
              cursor: 'pointer',
              marginBottom: 0,
              background: v.geeignet ? undefined : 'var(--grau-50)',
            }}
          >
            <input
              type="radio"
              style={{ width: 'auto', marginTop: 3 }}
              checked={gewaehlt === v.id}
              onChange={() => setzen(v.id)}
            />
            <span style={{ flex: 1 }}>
              <strong style={{ fontSize: '0.9rem' }}>{v.bezeichnung}</strong>
              <span style={{ color: 'var(--grau-500)', fontSize: '0.82rem', marginLeft: 8 }}>
                {v.zusatz}
              </span>
              {zeigen(v).length > 0 && (
                <div style={{ marginTop: 4 }}>
                  {zeigen(v).map((b, i) => (
                    <span
                      key={i}
                      className={`etikett ${b.schwere === 'SPERRE' ? 'rot' : 'orange'}`}
                      style={{ marginRight: 4 }}
                    >
                      {b.meldung}
                    </span>
                  ))}
                </div>
              )}
            </span>
          </label>
        ))}
      </div>
    )
  }

  return (
    <div className="karte abschnitt" style={{ borderLeft: '4px solid var(--tuerkis-500)' }}>
      <h2>
        Kurs {zeile.kursnummer} {reiter === 'tauschen' ? 'tauschen' : 'besetzen'}
        <span style={{ fontWeight: 400, color: 'var(--grau-500)', marginLeft: 12, fontSize: '0.9rem' }}>
          {zeile.dienstbeginn !== null &&
            `${alsUhrzeit(zeile.dienstbeginn)} – ${alsUhrzeit(zeile.dienstende)}`}
          {zeile.fahrzeuggruppe && ` · Fahrzeuggruppe ${zeile.fahrzeuggruppe}`}
        </span>
      </h2>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          className={reiter === 'besetzen' ? 'knopf-primaer' : 'knopf-schlicht'}
          style={{ padding: '6px 14px' }}
          onClick={() => setReiter('besetzen')}
        >
          Fahrer und Fahrzeug
        </button>
        <button
          className={reiter === 'tauschen' ? 'knopf-primaer' : 'knopf-schlicht'}
          style={{ padding: '6px 14px' }}
          onClick={() => setReiter('tauschen')}
        >
          Mit einem anderen Dienst tauschen
        </button>
      </div>

      {fehler && <div className="meldung fehler">{fehler}</div>}

      {sperrmeldung && (
        <div className="meldung fehler">
          <strong>Die Zuteilung verstößt gegen eine Sperre:</strong> {sperrmeldung}
          <div style={{ marginTop: 10 }}>
            <button
              className="knopf-schlicht"
              style={{ color: 'var(--rot-500)' }}
              disabled={speichert}
              onClick={() => void speichern(true)}
            >
              Trotzdem speichern
            </button>
            <span style={{ marginLeft: 10, fontSize: '0.82rem' }}>
              Der Vorgang wird protokolliert.
            </span>
          </div>
        </div>
      )}

      {reiter === 'tauschen' ? (
        <>
          {tauschsperre && (
            <div className="meldung fehler">
              <strong>Der Tausch verstößt gegen eine Sperre:</strong> {tauschsperre}
              <div style={{ marginTop: 10 }}>
                <button
                  className="knopf-schlicht"
                  style={{ color: 'var(--rot-500)' }}
                  disabled={speichert}
                  onClick={() => void tauschen(true)}
                >
                  Trotzdem tauschen
                </button>
                <span style={{ marginLeft: 10, fontSize: '0.82rem' }}>
                  Der Vorgang wird protokolliert.
                </span>
              </div>
            </div>
          )}

          {partner === null ? (
            <div className="laden">Tauschpartner werden geprüft …</div>
          ) : partner.length === 0 ? (
            <div className="meldung hinweis">
              An diesem Tag ist kein anderer Dienst besetzt — es gibt niemanden zum Tauschen.
            </div>
          ) : (
            <>
              <p style={{ color: 'var(--grau-500)', fontSize: '0.86rem', marginTop: 0 }}>
                {zeile.fahrerName
                  ? `${zeile.fahrerName} und der gewählte Fahrer wechseln die Dienste.`
                  : 'Der gewählte Fahrer übernimmt diesen Dienst; sein bisheriger wird frei.'}{' '}
                Das Fahrzeug bleibt jeweils am Kurs.
              </p>

              <div
                style={{
                  border: '1px solid var(--grau-200)',
                  borderRadius: 'var(--radius-klein)',
                  overflow: 'hidden',
                }}
              >
                {partner.map((p) => (
                  <label
                    key={p.kursId}
                    style={{
                      display: 'flex',
                      gap: 9,
                      padding: '9px 12px',
                      borderBottom: '1px solid var(--grau-100)',
                      fontWeight: 400,
                      cursor: 'pointer',
                      marginBottom: 0,
                      background: p.geeignet ? undefined : 'var(--grau-50)',
                    }}
                  >
                    <input
                      type="radio"
                      style={{ width: 'auto', marginTop: 3 }}
                      checked={gewaehlterPartner === p.kursId}
                      onChange={() => setGewaehlterPartner(p.kursId)}
                    />
                    <span style={{ flex: 1 }}>
                      <strong style={{ fontSize: '0.9rem' }}>{p.fahrerName}</strong>
                      <span
                        style={{
                          color: 'var(--grau-500)',
                          fontSize: '0.82rem',
                          marginLeft: 8,
                        }}
                      >
                        Kurs {p.kursnummer}
                        {p.dienstbeginn !== null &&
                          ` · ${alsUhrzeit(p.dienstbeginn)} – ${alsUhrzeit(p.dienstende)}`}
                      </span>
                      {p.befunde.length > 0 && (
                        <div style={{ marginTop: 4 }}>
                          {p.befunde.map((b, i) => (
                            <span
                              key={i}
                              className={`etikett ${b.schwere === 'SPERRE' ? 'rot' : 'orange'}`}
                              style={{ marginRight: 4 }}
                            >
                              {b.meldung}
                            </span>
                          ))}
                        </div>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button
              className="knopf-primaer"
              disabled={speichert || gewaehlterPartner === null}
              onClick={() => void tauschen(false)}
            >
              {speichert ? 'Wird getauscht …' : 'Tauschen'}
            </button>
            <button className="knopf-schlicht" onClick={onAbbruch}>
              Abbrechen
            </button>
          </div>
        </>
      ) : laedt ? (
        <div className="laden">Vorschläge werden geprüft …</div>
      ) : (
        <>
          {kursbefunde.length > 0 && (
            <div className="meldung hinweis">
              <strong>Zum Kurs selbst:</strong>{' '}
              {kursbefunde.map((b) => b.meldung).join(' · ')}
            </div>
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: 18,
            }}
          >
            <div>
              <label>Fahrer</label>
              {liste(fahrer, gewaehlterFahrer, setGewaehlterFahrer, 'kein Fahrer')}
            </div>
            <div>
              <label>Fahrzeug</label>
              {liste(fahrzeuge, gewaehltesFahrzeug, setGewaehltesFahrzeug, 'kein Fahrzeug')}
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 14,
              marginTop: 16,
            }}
          >
            <div className="feld">
              <label htmlFor="status">Status</label>
              <select id="status" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="GEPLANT">geplant</option>
                <option value="BESTAETIGT">bestätigt</option>
                <option value="ABGESAGT">abgesagt</option>
              </select>
            </div>
            <div className="feld" style={{ gridColumn: 'span 2' }}>
              <label htmlFor="bemerkung">Bemerkung</label>
              <input
                id="bemerkung"
                type="text"
                value={bemerkung}
                onChange={(e) => setBemerkung(e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              className="knopf-primaer"
              disabled={speichert}
              onClick={() => void speichern(false)}
            >
              {speichert ? 'Wird gespeichert …' : 'Übernehmen'}
            </button>
            <button className="knopf-schlicht" onClick={onAbbruch}>
              Abbrechen
            </button>
          </div>
        </>
      )}
    </div>
  )
}
